import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { User, Mail, Phone, MapPin, CreditCard, Building, Save, Edit, Calendar, AlertCircle, CheckCircle } from 'lucide-react';

const Profile = ({ currentUser, token }) => { // Recibe currentUser y token como props (token ya desencriptado)
  const [userData, setUserData] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Campos editables (solo estos se envían en la actualización si cambian)
  const editableFields = ['tlfCasa', 'tlfCelular', 'email', 'fechaNacimiento', 'direccion']; // Solo estos se envían en el body

  useEffect(() => {
    if (currentUser) {
      // Prellena con datos del usuario autenticado
      const fullName = `${currentUser.nomPrimero || ''} ${currentUser.nomSegundo || ''} ${currentUser.apePaterno || ''} ${currentUser.apeMaterno || ''}`.trim();
      setUserData({
        ...currentUser,
        fullName: fullName || 'Usuario sin nombre',
      });
      setFormData({
        ...currentUser,
        fullName,
      });
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    setFormData(userData); // Inicializa el formulario con los datos del usuario
  }, [userData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Solo permite cambios en campos editables
    if (editableFields.includes(name)) {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setSaveLoading(true);

    try {
      // Prepara solo los campos editables para enviar, más idCliente para identificar
      const updateData = {
        idCliente: currentUser.idCliente, // Siempre incluido para identificar el cliente
      };
      editableFields.forEach(field => {
        if (formData[field] !== userData[field]) {
          updateData[field] = formData[field];
        }
      });

      if (Object.keys(updateData).length === 1) { // Solo idCliente, no hay cambios
        setSuccessMessage('No hay cambios para guardar.');
        setIsEditing(false);
        return;
      }

      // URL de tu API para actualizar perfil (¡CÁMBIALA por la tuya!)
      const API_UPDATE_URL = 'https://appwebtienda-a6b3ecgnggbjh6c7.centralus-01.azurewebsites.net/api/autenticacion/update'; // Placeholder - reemplaza con tu endpoint

      const response = await fetch(API_UPDATE_URL, {
        method: 'PUT', // O 'PATCH' si tu API lo prefiere
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`, // Token sin encriptar (plano) en el header
        },
        body: JSON.stringify(updateData), // Incluye idCliente + campos editables que cambiaron
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Éxito: actualiza el estado local con los nuevos datos
        setUserData(prev => ({ ...prev, ...updateData }));
        setFormData(prev => ({ ...prev, ...updateData }));
        setSuccessMessage('¡Perfil actualizado con éxito!');
        setIsEditing(false);
      } else {
        setError(data.message || 'Error al actualizar el perfil. Inténtalo de nuevo.');
      }
    } catch (err) {
      setError('Error de conexión. Verifica tu internet o la API.');
      console.error('Error al actualizar perfil:', err);
    } finally {
      setSaveLoading(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Cargando perfil...</div>;
  }

  if (!currentUser) {
    return <div className="text-center py-8">No hay datos de usuario disponibles.</div>;
  }

  const isNonEditable = (fieldName) => !editableFields.includes(fieldName);

  return (
    <div className="container mx-auto px-4 py-8">
      <motion.h1
        className="text-4xl font-extrabold text-gray-900 mb-8 text-center bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        Mi Perfil
      </motion.h1>

      <motion.div
        className="bg-white/90 backdrop-blur-xl border border-gray-200/50 rounded-3xl p-8 shadow-2xl max-w-2xl mx-auto"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <div className="flex justify-end mb-4">
          {!isEditing ? (
            <motion.button
              onClick={() => setIsEditing(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg font-semibold shadow-md hover:bg-blue-600 transition-colors duration-200"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              disabled={loading || saveLoading}
            >
              <Edit className="w-5 h-5" />
              Editar
            </motion.button>
          ) : (
            <motion.button
              onClick={handleSave}
              className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg font-semibold shadow-md hover:bg-green-600 transition-colors duration-200"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              disabled={saveLoading}
            >
              <Save className="w-5 h-5" />
              {saveLoading ? 'Guardando...' : 'Guardar'}
            </motion.button>
          )}
        </div>

        {/* Mensajes de éxito/error */}
        {successMessage && (
          <motion.div
            className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg flex items-center gap-2"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <CheckCircle className="w-5 h-5" />
            {successMessage}
          </motion.div>
        )}
        {error && (
          <motion.div
            className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg flex items-center gap-2"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <AlertCircle className="w-5 h-5" />
            {error}
          </motion.div>
        )}

        <form onSubmit={handleSave} className="space-y-6">
          {/* Grid para nombres y apellidos */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">Primer Nombre</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  name="nomPrimero"
                  value={formData.nomPrimero || ''}
                  onChange={handleChange}
                  readOnly={true} // Siempre no editable
                  className="w-full pl-10 pr-4 py-3 border rounded-xl shadow-sm bg-gray-100 border-gray-300 text-gray-600 cursor-not-allowed focus:outline-none transition-all duration-300"
                />
              </div>
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">Segundo Nombre</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  name="nomSegundo"
                  value={formData.nomSegundo || ''}
                  onChange={handleChange}
                  readOnly={true} // Siempre no editable
                  className="w-full pl-10 pr-4 py-3 border rounded-xl shadow-sm bg-gray-100 border-gray-300 text-gray-600 cursor-not-allowed focus:outline-none transition-all duration-300"
                />
              </div>
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">Apellido Paterno</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  name="apePaterno"
                  value={formData.apePaterno || ''}
                  onChange={handleChange}
                  readOnly={true} // Siempre no editable
                  className="w-full pl-10 pr-4 py-3 border rounded-xl shadow-sm bg-gray-100 border-gray-300 text-gray-600 cursor-not-allowed focus:outline-none transition-all duration-300"
                />
              </div>
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">Apellido Materno</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  name="apeMaterno"
                  value={formData.apeMaterno || ''}
                  onChange={handleChange}
                  readOnly={true} // Siempre no editable
                  className="w-full pl-10 pr-4 py-3 border rounded-xl shadow-sm bg-gray-100 border-gray-300 text-gray-600 cursor-not-allowed focus:outline-none transition-all duration-300"
                />
              </div>
            </div>
          </div>

          {/* Razón Social justo debajo de apellidos, en ancho completo */}
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">Razón Social</label>
            <div className="relative">
              <Building className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                name="razonSocial"
                value={formData.razonSocial || ''}
                onChange={handleChange}
                readOnly={true} // Siempre no editable
                className="w-full pl-10 pr-4 py-3 border rounded-xl shadow-sm bg-gray-100 border-gray-300 text-gray-600 cursor-not-allowed focus:outline-none transition-all duration-300"
              />
            </div>
          </div>

          {/* Grid para DNI, RUC y otros */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">DNI</label>
              <div className="relative">
                <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  name="dniCliente"
                  value={formData.dniCliente || ''}
                  onChange={handleChange}
                  readOnly={true} // Siempre no editable
                  className="w-full pl-10 pr-4 py-3 border rounded-xl shadow-sm bg-gray-100 border-gray-300 text-gray-600 cursor-not-allowed focus:outline-none transition-all duration-300"
                />
              </div>
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">RUC</label>
              <div className="relative">
                <Building className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  name="rucCliente"
                  value={formData.rucCliente || ''}
                  onChange={handleChange}
                  readOnly={true} // Siempre no editable
                  className="w-full pl-10 pr-4 py-3 border rounded-xl shadow-sm bg-gray-100 border-gray-300 text-gray-600 cursor-not-allowed focus:outline-none transition-all duration-300"
                />
              </div>
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">Teléfono Fijo</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  name="tlfCasa"
                  value={formData.tlfCasa || ''}
                  onChange={handleChange}
                  readOnly={!isEditing}
                  className={`w-full pl-10 pr-4 py-3 border rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500/30 transition-all duration-300 text-gray-900 ${isEditing ? 'bg-white border-gray-300' : 'bg-gray-100 border-gray-200'}`}
                />
              </div>
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">Teléfono Celular</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  name="tlfCelular"
                  value={formData.tlfCelular || ''}
                  onChange={handleChange}
                  readOnly={!isEditing}
                  className={`w-full pl-10 pr-4 py-3 border rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500/30 transition-all duration-300 text-gray-900 ${isEditing ? 'bg-white border-gray-300' : 'bg-gray-100 border-gray-200'}`}
                />
              </div>
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="email"
                  name="email"
                  value={formData.email || ''}
                  onChange={handleChange}
                  readOnly={!isEditing}
                  className={`w-full pl-10 pr-4 py-3 border rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500/30 transition-all duration-300 text-gray-900 ${isEditing ? 'bg-white border-gray-300' : 'bg-gray-100 border-gray-200'}`}
                />
              </div>
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">Fecha de Nacimiento</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="date"
                  name="fechaNacimiento"
                  value={formData.fechaNacimiento || ''}
                  onChange={handleChange}
                  readOnly={!isEditing}
                  className={`w-full pl-10 pr-4 py-3 border rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500/30 transition-all duration-300 text-gray-900 ${isEditing ? 'bg-white border-gray-300' : 'bg-gray-100 border-gray-200'}`}
                />
              </div>
            </div>
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">Dirección</label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                name="direccion"
                value={formData.direccion || ''}
                onChange={handleChange}
                readOnly={!isEditing}
                className={`w-full pl-10 pr-4 py-3 border rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500/30 transition-all duration-300 text-gray-900 ${isEditing ? 'bg-white border-gray-300' : 'bg-gray-100 border-gray-200'}`}
              />
            </div>
          </div>
          {/* Razón Comercial ocultada completamente */}
        </form>
      </motion.div>
    </div>
  );
};

export default Profile;